import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePermissions } from '../context/PermissionContext';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import { GENERATE_AUTH_TOKEN } from '../config';

interface CompanyRegistrationData {
  company_name: string;
  industry: string;
  address?: string; // Made optional since field is temporarily commented out
  contact_phone?: string; // Made optional since field is temporarily commented out
  logo_url?: string;
}

interface ServerResponse {
  response: 'success' | 'error';
  description: string;
}

export const OwnerOnboarding = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationData, setRegistrationData] = useState<CompanyRegistrationData>({
    company_name: '',
    industry: 'Moda e Indumentaria', // Set default industry to 'Moda e Indumentaria'
    // address field is now optional and omitted from initial state
    // contact_phone field is now optional and omitted from initial state
  });

  // Rubro select with custom option ("Otro") support
  const rubroOptions = [
    'Moda e Indumentaria',
    'Hogar y Decoración',
    'Belleza y Cuidado Personal',
    'Tecnología',
    'Salud y Bienestar',
    'Deportes y Fitness',
    'Automotriz',
    'Gastronomía',
    'Otro',
  ];
  const [industryOption, setIndustryOption] = useState<string>('Moda e Indumentaria');
  const [customIndustry, setCustomIndustry] = useState<string>('');

  useEffect(() => {
    const val = registrationData.industry;
    if (rubroOptions.includes(val)) {
      setIndustryOption(val);
      setCustomIndustry('');
    } else if (val && val.trim().length > 0) {
      setIndustryOption('Otro');
      setCustomIndustry(val);
    }
  }, [registrationData.industry]);

  // Get access code from location state if available

  const validateField = (value: string): boolean => {
    // Check for special characters except @ and . for email
    const specialCharsRegex = /[!#$%^&*()_+\-=\[\]{};':"\\|,<>/?]/;
    return !specialCharsRegex.test(value);
  };

  const validateForm = (): string | null => {
    // Check for empty fields
    for (const [key, value] of Object.entries(registrationData)) {
      // Logo, address, contact_phone are optional
      if (key === 'logo_url' || key === 'address' || key === 'contact_phone') continue; 
      if (!value && typeof value === 'string') {
        return `El campo ${key.replace('_', ' ')} es obligatorio`;
      }
    }

    /* Phone validation temporarily commented out
    // Validate phone format (allow numbers, spaces, and + symbol)
    const phoneRegex = /^[0-9\s+]+$/;
    if (registrationData.contact_phone && !phoneRegex.test(registrationData.contact_phone)) {
      return 'El teléfono solo puede contener números, espacios y el símbolo +';
    }
    */

    // Check for special characters in other fields (narrow to required string keys)
    const fieldsToValidate: Array<keyof Pick<CompanyRegistrationData, 'company_name' | 'industry'>> = ['company_name', 'industry'];
    for (const field of fieldsToValidate) {
      if (!validateField(registrationData[field])) {
        return `El campo ${String(field).replace('_', ' ')} no puede contener caracteres especiales`;
      }
    }

    return null;
  };

  const handleRegisterCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const dataWithUserId = {
        ...registrationData,
        // contact_phone field is temporarily commented out
        user_id: user.id
      };

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/analizar_nombre_y_logo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(dataWithUserId),
      });

      const data: ServerResponse = await response.json();

      if (data.response === 'error') {
        throw new Error(data.description);
      }

      if (data.response === 'success') {
        // Refresh permissions before navigating to ensure they're updated
        await refreshPermissions();

        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const companyId = (profile as any)?.id_company as string | null;
          if (companyId) {
            await supabase
              .from('company_profiles')
              .update({ rubro: registrationData.industry })
              .eq('id_company', companyId);
            mergeLastCrearAnuncioState({ rubro: registrationData.industry });
          }
        } catch {}

        navigate('/add-first-product');
      }
    } catch (err) {
      console.error('Error registering company:', err);
      setError(err instanceof Error ? err.message : 'Error al registrar la empresa');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white md:bg-[#EFF0F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md md:max-w-2xl">

        {/* Registration Form */}
        <div className="bg-white rounded-none md:rounded-xl shadow-none md:shadow-lg p-8">
            <h2 className="text-3xl md:text-2xl font-semibold text-gray-900 mb-8">
              ¿Y cómo se llama tu marca?
            </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleRegisterCompany} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la marca
                </label>
                <input
                  type="text"
                  value={registrationData.company_name}
                  onChange={(e) => setRegistrationData({...registrationData, company_name: e.target.value})}
                  className="w-full h-12 px-5 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rubro
                </label>
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="relative md:w-1/2">
                    <select
                      value={industryOption}
                      onChange={(e) => {
                        const v = e.target.value;
                        setIndustryOption(v);
                        if (v !== 'Otro') {
                          setCustomIndustry('');
                          setRegistrationData({ ...registrationData, industry: v });
                        } else {
                          setRegistrationData({ ...registrationData, industry: customIndustry });
                        }
                      }}
                      className="w-full h-12 px-5 pr-9 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                      required
                    >
                      {rubroOptions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  {industryOption === 'Otro' && (
                    <input
                      type="text"
                      value={customIndustry}
                      onChange={(e) => {
                        setCustomIndustry(e.target.value);
                        setRegistrationData({ ...registrationData, industry: e.target.value });
                      }}
                      placeholder="Especificá tu rubro"
                      className="w-full md:w-1/2 h-12 px-5 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  )}
                </div>
              </div>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Direccion field temporarily commented out as requested
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  value={registrationData.address}
                  onChange={(e) => setRegistrationData({...registrationData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>
              */}



              {/* Teléfono field temporarily commented out as requested
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono de Contacto (Opcional)
                </label>
                <input
                  type="tel"
                  value={registrationData.contact_phone}
                  onChange={(e) => setRegistrationData({...registrationData, contact_phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              */}

              {/* Access code field removed - now provided via the AccessCodePage */}
            </div>

            {loading && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md text-blue-600 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <p>Creando perfil de empresa. Este proceso puede tomar unos segundos</p>
              </div>
            )}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                type="submit"
                disabled={loading}
                className="order-1 md:order-2 w-full md:w-auto h-12 px-6 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando...
                  </>
                ) : (
                  'Continuar'
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/onboarding')}
                className="order-2 md:order-1 h-12 px-5 rounded-full text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Volver</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};